import type { ProgramInfiniteSessionApiGateway } from "@/modules/sessions/gateway/programInfiniteSessionApiGateway"
import {
  type WebSessionDetailsDtoWithNonNullable,
  WebSessionsPlanningDtoSchema,
  type CreateSessionInputDto,
  type UpdateSessionInputDto,
  type WebSessionsPlanningDto,
  WebSessionDetailsDtoWithNonNullableSchema,
} from "@/modules/sessions/gateway/programInfiniteSessionDto"
import type { BlockSessionInputDto } from "@/modules/sessions/gateway/sessionBlockDto"
import { LoggerService } from "@modules/common/logger/loggerService"
import { ApiClient } from "@modules/core/api/client/apiClient"
import { defaultDateFormat } from "@modules/utils/dateUtils"
import dayjs from "dayjs"

export class ProgramInfiniteSessionApi implements ProgramInfiniteSessionApiGateway {
  private sessionBaseUrl = "/sessions"

  constructor(private api: ApiClient) {}

  async getDetailedSession(id: string): Promise<WebSessionDetailsDtoWithNonNullable> {
    const resultDto = await this.api.get<WebSessionDetailsDtoWithNonNullable>(`${this.sessionBaseUrl}/${id}`)
    const resultParsed = WebSessionDetailsDtoWithNonNullableSchema.safeParse(resultDto.data)
    if (!resultParsed.success) {
      console.error("Invalid session data", resultParsed.error)
      throw new Error(`Invalid session data: ${JSON.stringify(resultParsed.error)}`)
    }
    return resultParsed.data
  }

  async getSessions(
    startDate: string,
    endDate: string,
    programId: string,
  ): Promise<WebSessionDetailsDtoWithNonNullable[]> {
    const result = await this.api.get<WebSessionsPlanningDto>(this.sessionBaseUrl, {
      params: {
        startDate: dayjs(startDate).format(defaultDateFormat),
        endDate: dayjs(endDate).format(defaultDateFormat),
        programId,
      },
    })

    const resultParsed = WebSessionsPlanningDtoSchema.safeParse(result.data)
    if (!resultParsed.success) {
      throw new Error(`Invalid sessions planning data: ${JSON.stringify(resultParsed.error)}`)
    }
    return resultParsed.data.sessions
  }

  async createSession(sessionDraft: CreateSessionInputDto) {
    const resultDto = await this.api.post<WebSessionDetailsDtoWithNonNullable>(this.sessionBaseUrl, sessionDraft)
    return LoggerService.parseApiDto(WebSessionDetailsDtoWithNonNullableSchema, resultDto.data)
  }

  async updateSession(id: string, sessionDraft: UpdateSessionInputDto) {
    const resultDto = await this.api.put<WebSessionDetailsDtoWithNonNullable>(
      `${this.sessionBaseUrl}/${id}`,
      sessionDraft,
    )
    return LoggerService.parseApiDto(WebSessionDetailsDtoWithNonNullableSchema, resultDto.data)
  }

  async deleteSession(id: string) {
    await this.api.delete(`${this.sessionBaseUrl}/${id}`)
  }

  async changeSessionIndex(id: string, position: number) {
    await this.api.put(`${this.sessionBaseUrl}/${id}/reorder`, {
      sessionNewIndex: position,
    })
  }

  async copyWeekFromAnInfiniteProgramToAnother(
    originProgramId: string,
    destinationProgramId: string,
    originStartDate: string,
    originEndDate: string,
    destinationStartDate: string,
    overwrite = false,
  ) {
    await this.api.post(`${this.sessionBaseUrl}/continuous-duplicate-week`, {
      originProgramId,
      destinationProgramId,
      originStartDate: dayjs(originStartDate).format("YYYY-MM-DD"),
      originEndDate: dayjs(originEndDate).format("YYYY-MM-DD"),
      destinationStartDate: dayjs(destinationStartDate).format("YYYY-MM-DD"),
      overwrite,
    })
  }

  async moveBlock(sessionId: string, oldOrderIndex: number, newOrderIndex: number) {
    await this.api.put(`${this.sessionBaseUrl}/${sessionId}/block/orderIndex`, {
      oldOrderIndex,
      newOrderIndex,
    })
  }

  async updateBlock(sessionId: string, block: BlockSessionInputDto) {
    await this.api.put(`${this.sessionBaseUrl}/${sessionId}/block`, block)
  }

  async setSessionCompleted(sessionId: string, completed: boolean): Promise<void> {
    await this.api.post(`${this.sessionBaseUrl}/${sessionId}/set-completed`, { completed })
  }
}
